import { MasterpassCard, MasterpassResponse } from 'Models/MasterpassSdkModels';
import { MasterPassV1CardType } from 'Models';
import handleFormData from '../MasterPassHtlmHelper';
import getActiveLanguageForMasterPass from '../MasterPassLanguageHelper';
import getEnvironment from '../GetEnvironment';
import getPublicIpFromStorage from '../GetPublicIpFromStorage';
import { MasterPassAccountStatusEnum } from '../Enums';
import { MasterpassAdapter, MasterpassAuthorizeParams, MasterpassSession } from './Types';
import { defaultTerminalGroupId } from './TerminalGroup';

/**
 * Eski MFS SDK adaptörü (`window.MFS`) — Masterpass v1.
 *
 * Backend `IsVersion2Active: false` döndüğünde uygulama bu adaptöre düşer.
 * Kaynak: v2 geçişinden önceki son çalışan sürüm (commit `b471f9a`).
 *
 * ─── v1'İN v2'DEN TEMEL FARKLARI ──────────────────────────────────────────────
 *
 * 1. GİRDİ BİÇİMİ — v1 JSON almaz. Alanları DOM'da gizli bir `<form>`a yazıp
 *    form elemanını SDK'ya verir (`MasterPassHtlmHelper`, jQuery gerektirir).
 *    Bu yüzden her metotta v2 isteği önce düz bir alan sözlüğüne çevrilir.
 *
 * 2. TOKEN — v2'de global olarak bir kez kurulur (`setToken`). v1'de token ve
 *    referans numarası HER İSTEĞİN GÖVDESİNE elle konur ve işlem başınadır.
 *
 * 3. KİMLİK — v2 JWT claim'lerinden okur, v1'in tek kimliği `msisdn`'dir.
 *
 * 4. KART SEÇİMİ — v2 `cardAlias` kullanır. v1 iki farklı ad kullanır:
 *    ödemede `listAccountName`, kart kayıt/silmede `accountAliasName`.
 *
 * 5. KAYITSIZ KARTLA ÖDEME — v2'de ayrı metot (`directPayment`). v1'de aynı
 *    `purchase` metodudur; gövdede `rtaPan` varsa SDK `directPurchase`'a yönlenir.
 *
 * 6. CEVAP — v1 kök seviyede `responseCode` / `responseDescription` / `cards`
 *    döner ve BAŞARIYI BOŞ STRING İLE DE ifade edebilir. Ortak biçime çeviri
 *    `toV2Response()` ile yapılır.
 */

/** SDK'nın beklediği form kimlikleri (eski `MasterPassFormEnum`). */
const FORM = {
  CHECK_ACCOUNT: 'checkMP-form',
  OTP: 'otp-form',
  DELETE_CARD: 'delete-form',
  LINK_TO_MERCHANT: 'linkCardtoMP-form',
  REGISTER: 'register-form',
  PURCHASE: 'purchase-form',
  RECURRING: 'recurringPayment-form',
} as const;

/**
 * Üye işyeri kimliği. v2'de JWT'den gelir; v1'de SDK'ya `setClientId` ile
 * kurulur ve ayrıca isteklerin gövdesinde de `clientId` olarak gider.
 */
export const v1ClientId = (): string => (getEnvironment() ? '34705878' : '34706580');

/**
 * ⚠️ v1'İN DİL KODU BİÇİMİ FARKLIDIR — `getActiveLanguageForMasterPass()`
 * DOĞRUDAN KULLANILAMAZ.
 *
 * O yardımcı v2 için `'tr-TR'` / `'en-US'` döndürür; v2'nin `setLanguage()`
 * metodu tire içermeyen değeri sessizce `'en-US'`e düşürdüğü için öyle olmak
 * zorunda. v1 ise `'tur'` / `'eng'` bekler ve tireli değeri doğrulamadan
 * geçirmez — `sendSmsLanguage` her v1 isteğinde bulunduğu için yanlış biçim
 * kart listeleme dahil TÜM çağrıları etkiler.
 */
const v1SmsLanguage = (): 'tur' | 'eng' => (getActiveLanguageForMasterPass() === 'en-US' ? 'eng' : 'tur');

/** v1 isteklerinin tamamında tekrarlanan sabit alanlar. */
const commonFields = () => ({
  clientId: v1ClientId(),
  dateTime: new Date(),
  version: '34',
  clientType: '1',
  sendSmsLanguage: v1SmsLanguage(),
});

/** v1 kart alanlarını ortak (v2) kart modeline çevirir. */
export const normalizeV1Card = (card: MasterPassV1CardType): MasterpassCard => ({
  cardAlias: card.Name,
  maskedCardNumber: card.Value1,
  uniqueCardNumber: card.UniqueId,
  cardState: card.CardStatus,
  productName: card.ProductName,
  cardIssuerIcaNumber: card.BankIca,
});

/**
 * v1 cevabını ortak (v2) biçime çevirir.
 *
 * ⚠️ v1'de `responseCode` BOŞ STRING gelebiliyor ve bu da başarı demektir —
 * eski kodda her yerde `res.responseCode == '0000' || res.responseCode == ''`
 * kontrolü vardı. Boş değer burada `'0000'`e sabitlenir ki ortak başarı kontrolü
 * (`isMasterpassSuccess`) tek kurala göre çalışsın.
 *
 * ⚠️ v1'in 3D adresleri (`url3D`) cevabın KENDİ gövdesindedir, v2'deki gibi bir
 * akış durumunda değil. Bu yüzden son cevap `lastResponse`'ta saklanıp
 * `flow3d()` ile sunulur.
 */
export const toV2Response = (raw: any): MasterpassResponse<any> => {
  const responseCode = raw?.responseCode === '' || raw?.responseCode === undefined ? '0000' : raw.responseCode;

  return {
    statusCode: responseCode === '0000' ? 200 : 202,
    responseCode,
    description: raw?.responseDescription,
    token: raw?.token,
    // ⚠️ v1 büyük `D` ile `url3D` kullanır, v2 küçük `d` ile `url3d`.
    url3d: raw?.url3D,
    result: {
      cards: Array.isArray(raw?.cards) ? raw.cards.map(normalizeV1Card) : undefined,
      accountStatus: raw?.accountStatus,
      orderNo: raw?.orderNo,
      referenceNo: raw?.referenceNo,
    },
  };
};

let session: MasterpassSession | undefined;
/** v1'de 3D adresleri cevapta geldiği için son ham cevap saklanır. */
let lastResponse: any;

/** SDK yüklü değilken ya da senkron exception'da üretilen yapay hata cevabı. */
const localError = (message: string): MasterpassResponse<any> => ({
  statusCode: 0,
  responseCode: 'SDK_ERROR',
  description: message,
  result: {},
  exception: { code: 'SDK_ERROR', message },
});

/**
 * MFS çağrılarını sarmalar:
 *  - SDK yüklü değilse ya da çağrı senkron exception fırlatırsa hata cevabı üretir,
 *  - başarılı cevabı ortak biçime çevirir ve `lastResponse`'a yazar.
 */
const invoke = (
  method: string,
  callback: (status: number, response: MasterpassResponse<any>) => void,
  run: (respond: (status: any, raw: any) => void) => void
) => {
  if (typeof window === 'undefined' || window.MFS === undefined) {
    callback(0, localError('Masterpass v1 SDK (MFS) yüklenemedi.'));
    return;
  }

  const respond = (status: any, raw: any) => {
    lastResponse = raw;

    // ⚠️ `responseCode` alanı HİÇ YOKSA bu bir cevap değil, bozuk/boş bir
    // geri çağrıdır. `toV2Response` boş kodu başarı sayar (v1'de boş string
    // gerçekten başarı demek) — o tolerans yalnızca ALAN VARSA geçerli olmalı,
    // yoksa ağ hatası sessizce "başarılı" görünür.
    // ⚠️ `in` operatörü ilkel değerlerde TypeError fırlatır; cevap nesne değilse
    // önce onu elemek gerekiyor, yoksa hata SDK'nın callback'i içinde patlar ve
    // hiçbir aksiyon dispatch edilmeden akış sessizce ölür.
    if (raw === null || typeof raw !== 'object' || !('responseCode' in raw)) {
      console.error(`[Masterpass v1] "${method}" beklenmeyen cevap döndürdü:`, raw);
      callback(Number(status) || 0, localError(`Masterpass v1: ${method} cevabı okunamadı.`));
      return;
    }

    callback(Number(status) || 0, toV2Response(raw));
  };

  try {
    run(respond);
  } catch (error: any) {
    console.error(`[Masterpass v1] "${method}" çağrısı hata verdi:`, error);
    callback(0, localError(error?.message ?? `Masterpass v1: ${method} başlatılamadı.`));
  }
};

const V1Adapter: MasterpassAdapter = {
  version: 'v1',

  get session() {
    return session;
  },

  /**
   * v1'de "yetkilendirme" diye ayrı bir istek yoktur: SDK `setClientId` ile
   * kurulur, token her isteğe tek tek eklenir. Burada yalnızca oturum bağlamı
   * kurulur ve kimlik msisdn'e sabitlenir.
   */
  authorize: ({ token, phone, context }: MasterpassAuthorizeParams) => {
    if (!token) {
      session = undefined;
      return undefined;
    }

    const clientId = v1ClientId();

    if (typeof window !== 'undefined' && window.MFS?.setClientId) {
      window.MFS.setClientId(clientId);
      // ⚠️ Test ortamında SDK'nın kendi varsayılan adresi kullanılır; yalnızca
      // canlıda üretim adresi kurulur. Eski App.tsx ile birebir aynı davranış.
      if (!getEnvironment()) {
        window.MFS.setAddress?.('https://ui.masterpassturkiye.com/v2');
      }
    }

    session = {
      version: 'v1',
      token,
      merchantId: clientId,
      accountKey: phone,
      accountKeyType: 'Msisdn',
      // ⚠️ v1'de `userId` MSISDN'dir, müşteri numarası DEĞİL — eski çalışan kod
      // `checkData.userId = phone` yapıyordu ve alan yalnızca `checkMasterPass`
      // çağrısında kullanılıyor. (v2 aynı alana müşteri numarasını bekliyor.)
      userId: phone,
      context,
    };

    return session;
  },

  /**
   * ⚠️ v1 token'ı İŞLEM BAŞINA üretilir; ikinci bir işlemde kullanılamaz.
   * Bu yüzden token önbelleği v1'de tamamen kapalıdır.
   */
  canReuseToken: () => false,

  flowToken: () => window?.MFS?.getLastToken?.(),

  /**
   * ⚠️ v1'de 3D adresleri akış durumunda değil, son cevabın gövdesinde gelir.
   * `url3DSuccess` / `url3DError` alanları v2'de `FlowDirectable`'ın karşılığıdır.
   */
  flow3d: () => ({
    url3d: lastResponse?.url3D,
    url3dSuccess: lastResponse?.url3DSuccess,
    url3dFail: lastResponse?.url3DError,
  }),

  /* ---------------------------------------------------------------- *
   * Hesap ve kart
   * ---------------------------------------------------------------- */

  /**
   * Kayıtlı kartları listeler.
   * ⚠️ Tek MFS metodu form ALMAZ — msisdn ve token doğrudan parametre olarak gider.
   */
  accountAccess: (request, callback) =>
    invoke('accountAccess', callback, (respond) =>
      window.MFS.listCards(request.accountKey ?? session?.accountKey ?? '', session?.token ?? '', respond)
    ),

  /**
   * Hesap durumu sorgusu.
   * ⚠️ v2'nin aksine kart listesi DÖNDÜRMEZ; yalnızca `accountStatus` verir.
   */
  checkAccount: (request, callback) =>
    invoke('checkAccount', callback, (respond) => {
      const json = {
        ...commonFields(),
        token: session?.token ?? '',
        userId: request.userId ?? session?.userId ?? '',
        msisdn: request.accountKey ?? session?.accountKey ?? '',
        // ⚠️ SDK bu iki alanı formdan OKUYOR; form'da yoksa servise `null` gider
        // ve hesap durumu sorgusu doğrulama hatasıyla döner. Eski kod ikisini de
        // gönderiyordu (`checkMasterPassAccountNormalize`).
        sendSms: 'N',
        referenceNo: '',
      };

      window.MFS.checkMasterPass(handleFormData(json, FORM.CHECK_ACCOUNT), respond);
    }),

  /**
   * ⚠️ v1 hesap durumunu HAZIR string olarak döndürür (`accountStatus`), v2'de
   * ise cevabın farklı alanlarından türetilir. Burada gelen değer olduğu gibi
   * kullanılır; boşsa "hesap yok" sayılır.
   */
  accountStatus: (response) => response?.result?.accountStatus ?? MasterPassAccountStatusEnum.NO_ACCOUNT,

  /**
   * Masterpass hesabına kart ekler.
   * ⚠️ v2 `cardNumber`/`cvv`/`cardAlias` der; v1 `rtaPan`/`cvc`/`accountAliasName`.
   */
  addCard: (request, callback) =>
    invoke('addCard', callback, (respond) => {
      const json = {
        ...commonFields(),
        token: session?.token ?? '',
        referenceNo: (request as any).requestReferenceNumber ?? session?.context?.referenceNo ?? '',
        msisdn: request.accountKey ?? session?.accountKey ?? '',
        email: (request as any).email ?? '',
        rtaPan: request.cardNumber ?? '',
        expiryDate: request.expiryDate ?? '',
        cvc: request.cvv ?? '',
        accountAliasName: request.accountAliasName ?? '',
        uiChannelType: '6',
        timeZone: '01',
        sendSms: 'N',
        mobileAccountConfig: 'MWA',
        identityVerificationFlag: 'Y',
        defaultAccount: 'Y',
        cardTypeFlag: '05',
        eActionType: 'A',
        actionType: 'A',
      };

      window.MFS.register(handleFormData(json, FORM.REGISTER), respond);
    }),

  /** Kartı siler. ⚠️ v1'de hedef `accountAliasName` (kart adı). */
  removeCard: (request, callback) =>
    invoke('removeCard', callback, (respond) => {
      const json = {
        ...commonFields(),
        token: session?.token ?? '',
        referenceNo: session?.context?.referenceNo ?? '',
        msisdn: request.accountKey ?? session?.accountKey ?? '',
        accountAliasName: request.cardAlias ?? '',
        sendSms: 'N',
        fp: '',
      };

      window.MFS.deleteCard(handleFormData(json, FORM.DELETE_CARD), respond);
    }),

  /** Masterpass hesabını üye işyerine bağlar. */
  linkToMerchant: (request, callback) =>
    invoke('linkToMerchant', callback, (respond) => {
      const json = {
        ...commonFields(),
        token: session?.token ?? '',
        referenceNo: session?.context?.referenceNo ?? '',
        msisdn: request.accountKey ?? session?.accountKey ?? '',
        cardAliasName: null,
        sendSms: 'N',
        fp: '',
      };

      window.MFS.linkCardToClient(handleFormData(json, FORM.LINK_TO_MERCHANT), respond);
    }),

  /**
   * ⚠️ v1'de KARŞILIĞI YOKTUR — kart kaydı ve ödeme iki ayrı adımdır.
   * Saga bu metodu v1 akışında çağırmaz; yine de sessiz kalmamak için hata döner.
   */
  completeRegistration: (_request, callback) =>
    callback(0, localError('Masterpass v1: kart kaydı tamamlama adımı yoktur.')),

  /* ---------------------------------------------------------------- *
   * Ödeme
   * ---------------------------------------------------------------- */

  /** Kayıtlı kartla ödeme. ⚠️ v1'de kart `listAccountName` ile seçilir. */
  payment: (request, callback) =>
    invoke('payment', callback, (respond) => {
      const json = buildPaymentJson(request);

      window.MFS.setAdditionalParameters?.(toV1AdditionalParams(request));
      window.MFS.purchase(handleFormData(json, FORM.PURCHASE), respond);
    }),

  /**
   * Kayıtsız kartla ödeme.
   * ⚠️ v1'de ayrı metot yoktur: gövdede `rtaPan` bulunması SDK'yı `directPurchase`
   * yoluna sokar.
   */
  directPayment: (request, callback) =>
    invoke('directPayment', callback, (respond) => {
      const json = {
        ...buildPaymentJson(request),
        rtaPan: (request as any).cardNumber ?? '',
        expiryDate: (request as any).expiryDate ?? '',
        // ⚠️ Kayıtsız kart ödemesinde çağıran `cvc` anahtarını kullanıyor
        // (`MasterpassDirectPaymentRequest`), kart KAYDINDA ise `cvv`
        // (`MasterpassAddCardRequest`). İkisi de okunur; yoksa CVC boş gider.
        cvc: (request as any).cvc ?? (request as any).cvv ?? '',
        // Kayıtsız kart akışında SMS gönderimi kapatılır (eski davranış).
        sendSms: 'N',
      };
      delete (json as any).sendSmsMerchant;

      window.MFS.setAdditionalParameters?.(toV1AdditionalParams(request));
      window.MFS.directPurchase(handleFormData(json, FORM.PURCHASE), respond);
    }),

  /** ⚠️ v1'de KARŞILIĞI YOKTUR — kaydet ve öde tek işlemde yapılamaz. */
  registerAndPurchase: (_request, callback) =>
    callback(0, localError('Masterpass v1: kaydet+öde tek adımda desteklenmez.')),

  /* ---------------------------------------------------------------- *
   * Doğrulama
   * ---------------------------------------------------------------- */

  /** OTP / CVV doğrulaması. Eski karşılığı: `MFS.validateTransaction`. */
  verifyOtp: (request, callback) =>
    invoke('verifyOtp', callback, (respond) => {
      const json = {
        // ⚠️ ALAN ADI SÜRÜME GÖRE DEĞİŞİYOR. v2 SMS kodunu `otpCode`, CVV/RTA
        // doğrulamasını `validationCode` ile gönderiyor (`masterPassOtpNormalize`
        // adıma göre farklı anahtar üretiyor). v1'de İKİSİ DE `validationCode`.
        // Yalnızca `validationCode` okunursa SMS doğrulaması BOŞ kodla gider ve
        // her OTP başarısız olur.
        validationCode:
          (request as any).validationCode ?? (request as any).otpCode ?? (request as any).otp ?? '',
        // ⚠️ BOŞ GİDER. v1'in doğrulama isteği referans numarasını kendi akış
        // durumundan çözüyor; eski çalışan kod da burayı boş bırakıyordu
        // (`masterPassOtpNormalize`). Token cevabındaki `ReqRefNumber`'ı buraya
        // koymak doğrulamayı yanlış işleme bağlar.
        referenceNo: '',
        sendSms: 'N',
        sendSmsLanguage: v1SmsLanguage(),
        pinType: 'otp',
      };

      window.MFS.validateTransaction(handleFormData(json, FORM.OTP), respond);
    }),

  /**
   * OTP'yi yeniden gönderir.
   * ⚠️ v2'nin aksine token ve dil PARAMETREDİR ve token akıştan okunur.
   */
  resendOtp: (callback) =>
    invoke('resendOtp', callback, (respond) =>
      window.MFS.resendOtp(window.MFS.getLastToken?.(), v1SmsLanguage(), respond)
    ),

  /* ---------------------------------------------------------------- *
   * Otomatik talimat
   * ---------------------------------------------------------------- */

  /** Talimat kaydı. ⚠️ v1'de tek metot + `actionType` ile ayrışır: A = ekle. */
  recurringOrder: (request, callback) => recurringCall('A', request, callback),

  /** Talimat güncelleme — `actionType: 'U'`. */
  recurringOrderUpdate: (request, callback) => recurringCall('U', request, callback),

  /** Talimat silme — `actionType: 'D'`. */
  recurringOrderDelete: (request, callback) => recurringCall('D', request, callback),
};


/**
 * v2'nin yapılandırılmış ödeme bloklarını v1'in `additionalParams` gövdesine
 * geri çevirir.
 *
 * ⚠️ NEDEN GEREKLİ: aynı bilgi iki sürümde farklı şekilde taşınıyor.
 *   v1 → tek düz nesne, snake_case, dizi alanlar `*_arr` ekiyle
 *   v2 → `orderProductsDetails` / `orderDetails` / `buyerDetails` /
 *        `billDetails` / `deliveryDetails` blokları
 * Saga v2 biçimini üretiyor; çeviriyi burada yapmak saga'yı sürümden bağımsız
 * tutuyor. `setAdditionalParameters` çağrılmazsa v1 ödemesi sipariş kalemi
 * bulamayıp hata veriyor.
 *
 * ⚠️ `card_program_name`, `number_time`, `owner_time` alanları v2 gövdesinde
 * BİLEREK yok (sabit `MASTERCARD` değeri Visa/Troy kartlarda yanlış bilgi
 * veriyordu). v1 tarafında ise bu alanlarla çalışan kanıtlanmış bir sözleşme
 * var; v1'i "düzeltmek" yerine eski değerleri aynen gönderiyoruz.
 */
const toV1AdditionalParams = (request: any) => {
  const products = request?.orderProductsDetails ?? {};
  const order = request?.orderDetails ?? {};
  const buyer = request?.buyerDetails ?? {};
  const bill = request?.billDetails ?? {};
  const delivery = request?.deliveryDetails ?? {};

  return {
    order_price_type_arr: products.OrderPriceType ?? ['VIRTUAL'],
    order_product_name_arr: products.OrderProductName ?? ['Belbim Sanal Pos Ödemesi'],
    order_product_code_arr: products.OrderProductCode ?? ['BELBIM1'],
    order_qty_arr: products.OrderQty ?? ['1'],
    order_product_info_arr: products.OrderProductInfo ?? ['VPOSPURCHASE'],
    order_price_arr: products.OrderPrice ?? [request?.amount],
    order_shipping: order.Shipping ?? '0',
    order_timeout: order.Timeout ?? '123',
    basket_id: order.BasketId ?? '123456',
    vendor_id: order.VendorId ?? '123456',
    commission_Rate: order.CommisionRate ?? '0',
    payment_content: order.PaymentContent ?? 'payment_content',
    transaction_description: order.TransactionDescription ?? 'transaction_description',

    buyer_id: buyer.BuyerId ?? '123456',
    identity_number: buyer.IdentityNumber ?? '123456',
    last_login_date: buyer.LastLoginDate,
    registration_date: buyer.RegistrationDate,

    bill_first_name: bill.BillFirstName ?? 'fname',
    bill_last_name: bill.BillLastName ?? 'lname',
    bill_email: bill.BillEmail ?? 'test@test.com',
    bill_phone: bill.BillPhone ?? '901111111111',
    bill_country_code: bill.BillCountryCode ?? 'TR',
    bill_fax: bill.BillFax ?? '123213123',
    bill_address: bill.BillAddress ?? 'bill_address',
    bill_address2: bill.BillAddress2 ?? 'bill_address2',
    bill_zip_code: bill.BillZipCode ?? '124214',
    bill_city: bill.BillCity ?? 'ISTANBUL',
    bill_state: bill.BillState ?? 'bill_state',

    delivery_first_name: delivery.DeliveryFirstName ?? 'delivery_first_name',
    delivery_last_name: delivery.DeliveryLastName ?? 'delivery_last_name',
    delivery_email: delivery.DeliveryEmail ?? 'test@test.com',
    delivery_phone: delivery.DeliveryPhone ?? '902222222222',
    delivery_company: delivery.DeliveryCompany ?? 'company',
    delivery_address: delivery.DeliveryAddress ?? 'deliveryaddres',
    delivery_address2: delivery.DeliveryAddress2 ?? 'deliveryaddress2',
    delivery_zip_code: delivery.DeliveryZipCode ?? '325325',
    delivery_city: delivery.DeliveryCity ?? 'deliverycity',
    delivery_state: delivery.DeliveryState ?? 'deliverystate',
    delivery_country_code: delivery.DeliveryCountryCode ?? 'TR',

    // ⚠️ v2'de bilerek düşürülen, v1'de kanıtlanmış sözleşmenin parçası olan alanlar.
    card_program_name: 'MASTERCARD',
    number_time: '123',
    owner_time: '1234',
  };
};

/**
 * Ödeme isteklerinin ortak gövdesi.
 *
 * ⚠️ `amount` KURUŞ cinsindendir (TL × 100) ve bu, v2 ile AYNI kuraldır —
 * `masterPassPaymentNormalize` değeri zaten kuruşa çevirmiş hâlde veriyor.
 */
const buildPaymentJson = (request: any) => ({
  ...commonFields(),
  token: session?.token ?? '',
  referenceNo: request.requestReferenceNumber ?? session?.context?.referenceNo ?? '',
  msisdn: request.accountKey ?? session?.accountKey ?? '',
  // ⚠️ Mobil akış işlem tipini bu alanla bildiriyor; web akışında üretilmediği
  // için hiç gönderilmez (eski davranış).
  ...(request.paymentType ? { paymentType: request.paymentType } : {}),
  // ⚠️ v2 `cardAlias` der, v1 ödemede `listAccountName` bekler.
  listAccountName: request.cardAlias ?? request.listAccountName ?? '',
  amount: request.amount ?? 0,
  orderNo: request.orderNo ?? session?.context?.orderNo ?? '',
  // ⚠️ v2'de `terminalGroupId`, v1'de `macroMerchantId` — aynı değer.
  macroMerchantId: request.terminalGroupId ?? session?.context?.terminalGroupId ?? defaultTerminalGroupId(),
  installmentCount: '',
  rewardName: '',
  rewardValue: '',
  cvc: '',
  sendSms: 'Y',
  aav: 'aav',
  clientIp: getPublicIpFromStorage(),
  encCPin: '0',
  encPassword: '',
  sendSmsMerchant: 'Y',
  password: '',
});

/** Üç talimat işlemi de aynı MFS metoduna `actionType` ile gider. */
const recurringCall = (
  actionType: 'A' | 'U' | 'D',
  request: any,
  callback: (status: number, response: MasterpassResponse<any>) => void
) =>
  invoke(`recurringOrder(${actionType})`, callback, (respond) => {
    const json = {
      ...commonFields(),
      token: session?.token ?? '',
      // ⚠️ v2 sözleşmesi `requestReferenceNumber` der, v1 `referenceNo`.
      referenceNo:
        request.requestReferenceNumber ?? request.referenceNo ?? session?.context?.referenceNo ?? '',
      msisdn: request.accountKey ?? session?.accountKey ?? '',
      listAccountName: request.cardAlias ?? '',
      // ⚠️ v2 sözleşmesi `amountLimit` der, v1 `amount`. Yalnızca `amount`
      // okunursa talimat tutarı BOŞ gider ve kayıt ya reddedilir ya da 0 limitle
      // oluşur.
      amount: String(request.amountLimit ?? request.amount ?? ''),
      endDate: request.expiryDate ?? request.endDate ?? '',
      actionType,
      productId: request.productId ?? '',
      transactionFee: request.transactionFee ?? '',
      sendSms: 'N',
      fp: '',
    };

    window.MFS.initiateRecurringPayment(handleFormData(json, FORM.RECURRING), respond);
  });

export default V1Adapter;
